import React from 'react';
import MosaicField from 'shared/components/trodo-kit/MosaicField';

/* ─────────────────────────────────────────
 *  JarvisHero — typewriter heading only.
 *  No suggestion rows, no pill, no "pick one" instruction.
 *  JarvisBackdrop (the triangle field) exported separately.
 * ───────────────────────────────────────── */

const PROMPTS = [
  "Build me a weekly report on cost and failures by agent",
  "Create an eval that catches made-up answers",
  "Alert me when p95 latency passes 20 seconds",
  "Investigate this morning's failures and file an issue",
  "Find silent failures from the last 7 days and track them",
  "Improve my lowest-scoring eval and show me the change",
  "Compare this week's cost per agent to last week's",
  "Set up tracking for tool call timeouts",
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function useTypewriter(items, { typeMs = 30, deleteMs = 14, holdMs = 1800, gapMs = 240, enabled = true } = {}) {
  const [shown, setShown] = React.useState('');
  const [idx, setIdx] = React.useState(0);
  const idxRef = React.useRef(0);
  idxRef.current = idx;

  React.useEffect(() => {
    if (!enabled) { setShown(items[idxRef.current] || ''); return undefined; }
    let cancelled = false;
    const run = async () => {
      while (!cancelled) {
        const q = items[idxRef.current] || '';
        for (let i = 1; i <= q.length; i++) { if (cancelled) return; setShown(q.slice(0, i)); await sleep(typeMs); }
        await sleep(holdMs); if (cancelled) return;
        for (let i = q.length - 1; i >= 0; i--) { if (cancelled) return; setShown(q.slice(0, i)); await sleep(deleteMs); }
        await sleep(gapMs); if (cancelled) return;
        setIdx((n) => (n + 1) % items.length); await sleep(20);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [enabled, items, typeMs, deleteMs, holdMs, gapMs]);

  return shown;
}

/* ── Ambient backdrop: the brand's triangles, uncovered by moving the cursor ── */
export function JarvisBackdrop() {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(closest-side, rgb(var(--trodo-primary-rgb) / 0.12), transparent 70%)',
          filter: 'blur(8px)',
        }}
      />
      <MosaicField />
    </div>
  );
}

/* ── Main hero: typewriter heading only ── */
export default function JarvisHero({ disabled = false, compact = false }) {
  const reduceMotion = React.useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const shown = useTypewriter(PROMPTS, { enabled: !disabled && !reduceMotion });

  return (
    <div className={compact ? 'w-full mb-4' : 'w-full mb-10'}>
      <div
        className={`${compact ? 'text-[22px]' : 'text-[28px] sm:text-[36px] md:text-[44px]'} font-semibold tracking-tight leading-[1.15] min-h-[1.25em]`}
        style={{ color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}
        aria-live="polite"
        aria-label="Suggested prompt"
      >
        <span className="whitespace-pre-wrap">{shown || ' '}</span>
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: '3px',
            height: '0.88em',
            background: 'var(--trodo-primary)',
            borderRadius: '1px',
            verticalAlign: '-0.06em',
            marginLeft: '3px',
            boxShadow: '0 0 10px rgb(var(--trodo-primary-rgb) / 0.55)',
            animation: reduceMotion ? 'none' : 'jvCaret 0.95s steps(2,end) infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes jvCaret { 0%,49%{opacity:1} 50%,100%{opacity:0} }
      `}</style>
    </div>
  );
}
